import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Grid,
  Divider,
  IconButton,
  Collapse
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export default function IntroSummary() {
  const [isExecutiveExpanded, setExecutiveExpanded] = useState(false);
  const [isDetailedExpanded, setDetailedExpanded] = useState(false);

  const handleExecutiveExpandClick = () => {
    setExecutiveExpanded(!isExecutiveExpanded);
  };

  const handleDetailedExpandClick = () => {
    setDetailedExpanded(!isDetailedExpanded);
  };

  return (
    <>
      {/* Intro */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Fire Rebuild: Fire Risk Comparative Analysis App
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: -2, pt: 0 }}>
          Comparative Analysis of Fire and Fuel Risk in Relation to the 2025 Palisades Fire - Created by Michael Kureth / FireRebuild.com
        </Typography>
      </Box>

      {/* Executive Summary */}
      <Box sx={{ mb: 1 }}>
        <Card sx={{ mb: 1 }}>
          <Box onClick={handleExecutiveExpandClick} sx={{ bgcolor: "gray", color: "white", p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5">
                <strong>Executive Summary</strong>
              </Typography>
              <IconButton
                onClick={handleExecutiveExpandClick}
                aria-expanded={isExecutiveExpanded}
                aria-label="toggle executive summary"
                sx={{ color: 'white' }}
              >
                {isExecutiveExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={isExecutiveExpanded} timeout="auto" unmountOnExit>
            <CardContent>
              <Typography variant="body1" paragraph>
                If the burning of Pacific Palisades is excused because it is in a Very High Risk Fire Zone, how does your location compare?
                <br />
                Find out whether your home is part of the majority of California facing an even greater fire risk than Pacific Palisades.
              </Typography>
            </CardContent>
          </Collapse>
        </Card>
      </Box>

      {/* Detailed Summary */}
      <Box sx={{ mb: 1 }}>
        <Card sx={{ mb: 1 }}>
          <Box onClick={handleDetailedExpandClick} sx={{ bgcolor: "gray", color: "white", p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5">
                <strong>Detailed Summary</strong>
              </Typography>
              <IconButton
                onClick={handleDetailedExpandClick}
                aria-expanded={isDetailedExpanded}
                aria-label="toggle detailed summary"
                sx={{ color: 'white' }}
              >
                {isDetailedExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={isDetailedExpanded} timeout="auto" unmountOnExit>
            <CardContent>
              <Typography variant="body1" paragraph>
                Pacific Palisades is officially classified as a <strong>Very High Fire Hazard Severity Zone</strong> by California Department of Forestry and Fire Protection (CAL FIRE) and a <strong>Very High Fire Risk Zone</strong> by the California Public Utilities Commission.
                This assessment is made based on historical weather and fire risk conditions as well as fuel (vegetation) and terrain of the area.
                Communities in this range are at a significant and constant threat of being destroyed by a wildfire.
              </Typography>
              <Typography variant="body1" paragraph>
                This terrifying reality is not unique to Pacific Palisades. In fact, when Pacific Palisades is used as a benchmark for fire risk, <strong>the majority of California's residential areas face an equally high or even higher risk of being destroyed by fire.</strong>
              </Typography>
              <Typography variant="body1" paragraph>
                This application was created to give residents and communities across California a clear, data-driven comparison of wildfire risk. It enables users to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                <li>
                  <Typography variant="body1">
                    <strong>Check your fire risk</strong> anywhere in California.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>Compare your community’s conditions</strong> directly to Pacific Palisades.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>Understand both fire and fuel risks</strong> that contribute to overall wildfire danger.
                  </Typography>
                </li>
              </Box>
              <Typography variant="body1" paragraph>
                By translating complex wildfire data into an accessible comparative analysis, this tool empowers individuals and communities to stay informed, plan effectively, and build resilience in the face of California’s growing wildfire threat.
              </Typography>

              {/* Expandable Details */}
              <Accordion sx={{ mt: 4 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">More Details (How it Works)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" paragraph>
                    The app uses a comprehensive, data-driven approach to assess risk:
                  </Typography>
                  <Box component="ol" sx={{ pl: 3 }}>
                    <li>
                      <Typography variant="body2" paragraph>
                        <strong>Fire Risk Metrics</strong>: Core values like the Burning Index (BI), Energy Release Component (ERC), Reference Evapotranspiration (ETR), 100 Hour Dead Fuel Moisture (FM100), 1000 Hour Dead Fuel Moisture (FM1000), Maximum Relative Humidity (RMAX), Minimum Relative Humidity (RMIN), Specific Humidity (SPH), Potential Evapotranspiration (PET), and Mean Vapor Pressure Deficit (VPD) were measured in Pacific Palisades leading up to the fire.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" paragraph>
                        <strong>Historical Context</strong>: These values were compared against 17,013 days of historical data for Pacific Palisades, defining the threshold of a “Very High Fire Hazard Severity Zone.”
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" paragraph>
                        <strong>Statewide Analysis</strong>: This baseline was then compared with over 24,000 locations across California to place local conditions in context.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" paragraph>
                        <strong>Fuel & Brush Mapping</strong>: California wide vegetation data was analyzed alongside Pacific Palisades fuel conditions.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" paragraph>
                        <strong>Final Risk Score</strong>: By combining fire risk metrics with fuel mapping, the app provides a location-specific fire and fuel risk profile relative to Pacific Palisades.
                      </Typography>
                    </li>
                  </Box>

                  <Typography variant="body2" sx={{ pb: 0 }} paragraph>
                    <strong>Source</strong>
                  </Typography>

                  <Typography variant="body2" sx={{ pl: 2, mt: -1 }} paragraph>
                    Source data for fire risk is available at: https://www.climatologylab.org/gridmet.html
                    <br />
                    Source data for fuel and vegetation is available at: https://map.dfg.ca.gov/metadata/ds1327.html
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Collapse>
        </Card>
      </Box>
    </>
  );
}
